import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Statistic, Table, Select } from 'antd';
import EllipsisCell from '../../../components/common/EllipsisCell';
import { getChannelLabel } from '../../../utils/channelLabel';
import { PAYMENT_METHOD_OPTIONS } from '../../../constants/order';
import { DollarOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const COLORS = {
  brand: '#00A651',
  brandYellow: '#FFD100',
  blue: '#1890ff',
  purple: '#722ed1',
  orange: '#ff7a45',
  success: '#52C41A',
};

// 渠道柱状图调色板（循环使用）
const CHANNEL_COLORS = [COLORS.brand, COLORS.purple, COLORS.orange, COLORS.blue, COLORS.success, COLORS.brandYellow];

// 渠道显示名称映射已迁移到全局统一映射工具 utils/channelLabel.js（getChannelLabel），不再本地维护

// 支付方式显示名称映射：复用全局 PAYMENT_METHOD_OPTIONS，与订单列表/导出展示文案保持一致
const PAYMENT_LABELS = PAYMENT_METHOD_OPTIONS.reduce((map, opt) => {
  map[opt.value] = opt.label;
  return map;
}, {});

// 支付方式饼图调色板
const PAYMENT_COLORS = [COLORS.brand, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.success, COLORS.brandYellow];

/**
 * Module 5: Financial & Trends
 * Channel comparison + Cash reconciliation + Payment breakdown
 */
const FinancialTrends = ({ data }) => {
  const { t } = useTranslation();
  const [cashFilter, setCashFilter] = useState('all');

  if (!data) return null;

  // Grouped bar: Channel sales comparison (dynamic channels)
  const channelEntries = Object.entries(data.channelSales || {});
  const channelOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: 20, right: 20, top: 20, bottom: 30 },
    xAxis: {
      type: 'category',
      data: channelEntries.map(([key]) => getChannelLabel(t, key)),
    },
    yAxis: {
      type: 'value',
      name: 'THB',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: { show: false },
    },
    series: [{
      type: 'bar',
      data: channelEntries.map(([, value], idx) => ({
        value,
        itemStyle: { color: CHANNEL_COLORS[idx % CHANNEL_COLORS.length] },
      })),
      barWidth: 40,
      label: { show: true, position: 'top', formatter: (p) => `${(p.value / 1000).toFixed(0)}K`, fontWeight: 'bold' },
      itemStyle: { borderRadius: [4, 4, 0, 0] },
    }],
  };

  // Payment method pie（后端已只返回线上支付方式：CreditCard/TrueMoneyWallet/AMAZE_POINTS/TrueMoney/QRPayment）
  const paymentAllEntries = Object.entries(data.paymentMethods || {});
  // 图例固定展示全部支付方式选项，不论金额是否为0，保持选项列表稳定完整
  const paymentEntryNames = paymentAllEntries.map(([key]) => PAYMENT_LABELS[key] || key);
  // 饼图扇区仅展示金额大于0的条目，避免0值占位扇区
  const paymentEntries = paymentAllEntries.filter(([, value]) => Number(value) > 0);
  // 按支付方式key固定分配颜色，保证图例与饼图扇区颜色一致
  const paymentColorMap = paymentAllEntries.reduce((map, [key], idx) => {
    map[key] = PAYMENT_COLORS[idx % PAYMENT_COLORS.length];
    return map;
  }, {});
  const paymentPieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} THB ({d}%)' },
    legend: { bottom: 0, textStyle: { fontSize: 11 }, data: paymentEntryNames },
    series: [{
      type: 'pie',
      radius: ['35%', '65%'],
      center: ['50%', '42%'],
      label: { show: true, formatter: '{b}\n{d}%', fontSize: 11 },
      data: paymentEntries.map(([key, value]) => ({
        value,
        name: PAYMENT_LABELS[key] || key,
        itemStyle: { color: paymentColorMap[key] },
      })),
    }],
  };

  // Cash drill-down table columns
  const cashColumns = [
    { title: t('dashboard.financial.rider'), dataIndex: 'rider', key: 'rider', render: (text) => <EllipsisCell>{text}</EllipsisCell> },
    { title: t('dashboard.financial.channelCol'), dataIndex: 'channel', key: 'channel', render: (channel) => <EllipsisCell>{getChannelLabel(t, channel)}</EllipsisCell> },
    { title: t('dashboard.financial.posNo'), dataIndex: 'posNo', key: 'posNo', render: (text) => <EllipsisCell>{text}</EllipsisCell> },
    {
      title: t('dashboard.financial.deliveringCash'),
      dataIndex: 'delivering',
      key: 'delivering',
      render: (v) => (
        <EllipsisCell>
          <span style={{ color: COLORS.orange }}>THB {v.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
        </EllipsisCell>
      ),
    },
    {
      title: t('dashboard.financial.deliveredCash'),
      dataIndex: 'delivered',
      key: 'delivered',
      render: (v) => (
        <EllipsisCell>
          <span style={{ color: COLORS.success }}>THB {v.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
        </EllipsisCell>
      ),
    },
    {
      title: t('dashboard.total'),
      key: 'total',
      render: (_, row) => (
        <EllipsisCell>
          <b>THB {(row.delivering + row.delivered).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</b>
        </EllipsisCell>
      ),
    },
  ];

  // Generate rider filter options from data
  const riderOptions = [
    { label: t('dashboard.financial.allRiders'), value: 'all' },
    ...(data.cashOrdersByRider || []).map((row) => ({
      label: row.rider,
      value: row.rider,
    })),
  ];

  const filteredCashData = cashFilter === 'all'
    ? (data.cashOrdersByRider || [])
    : (data.cashOrdersByRider || []).filter((row) => row.rider === cashFilter);

  return (
    <Card title={t('dashboard.financial.title')} styles={{ body: { padding: 16 } }}>
      <Row gutter={16}>
        {/* Channel comparison */}
        <Col span={8}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, textAlign: 'center' }}>{t('dashboard.financial.channelSalesComparison')}</div>
          <ReactEChartsCore echarts={echarts} option={channelOption} style={{ height: 280 }} opts={{ renderer: 'canvas' }} />
        </Col>

        {/* Online payment pie */}
        <Col span={8}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, textAlign: 'center' }}>{t('dashboard.financial.onlinePaymentMethods')}</div>
          <ReactEChartsCore echarts={echarts} option={paymentPieOption} style={{ height: 280 }} opts={{ renderer: 'canvas' }} />
        </Col>

        {/* Cash Reconcile Panel */}
        <Col span={8}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              <DollarOutlined style={{ color: COLORS.brandYellow, marginRight: 4 }} />
              {t('dashboard.financial.cashReconciliation')}
            </div>
            <Select
              size="small"
              value={cashFilter}
              onChange={setCashFilter}
              style={{ width: 120 }}
              options={riderOptions}
            />
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <Card style={{ flex: 1, background: '#fffbe6', border: '1px solid #ffe58f' }} styles={{ body: { padding: 12 } }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>{t('dashboard.financial.totalCashAmount')}</span>}
                value={(data.paymentMethods || {}).POD ?? 0}
                prefix="THB"
                formatter={(v) => Number(v).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                valueStyle={{ fontSize: 22, fontWeight: 'bold', color: '#d48806' }}
              />
            </Card>
          </div>
          <Table
            columns={cashColumns}
            dataSource={filteredCashData}
            rowKey="rider"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content', y: 200 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default FinancialTrends;
